import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth.store';
import { useDevicesStore, Device, DevicePosition, PositionWaypoint } from '../store/devices.store';
import { useNotificationsStore, createNotificationFromAlert, createNotificationFromMissionEvent } from '../store/notifications.store';
import { useTenantContextStore } from '../store/tenant-context.store';

const WS_URL = import.meta.env.VITE_WS_URL || '';

let socket: Socket | null = null;

// Anti-duplicate: IMEIs that recently received a batch (ignore the following position:update)
const recentBatchImeis = new Map<string, number>();
const BATCH_DEDUP_MS = 500;

export function connectSocket(): Socket {
  // If socket exists but is disconnected, clean it up first
  if (socket && !socket.connected) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  if (socket?.connected) {
    return socket;
  }

  const token = useAuthStore.getState().accessToken;

  if (!token) {
    throw new Error('No auth token available');
  }

  const selectedTenant = useTenantContextStore.getState().selectedTenant;
  const auth: Record<string, string> = { token };
  if (selectedTenant) {
    auth.tenantId = selectedTenant.id;
  }

  socket = io(WS_URL, {
    auth,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('connect', () => {
    console.log('WebSocket connected');
    // Subscribe to all devices
    socket?.emit('subscribe:all');
    // Request current positions
    socket?.emit('request:positions');
  });

  socket.on('disconnect', (reason) => {
    console.log('WebSocket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('WebSocket connection error:', error);
  });

  // Handle position batch (multiple waypoints from a tracker batch)
  socket.on('position:batch', (data: {
    imei: string;
    waypoints: PositionWaypoint[];
    latestPosition: DevicePosition;
  }) => {
    recentBatchImeis.set(data.imei, Date.now());
    useDevicesStore.getState().updatePositionBatch(data.imei, data.waypoints, data.latestPosition);
  });

  // Handle position updates (single latest position — skip if a batch was just received)
  socket.on('position:update', (data: { imei: string; position: DevicePosition }) => {
    const batchTime = recentBatchImeis.get(data.imei);
    if (batchTime && Date.now() - batchTime < BATCH_DEDUP_MS) {
      return; // Ignore: batch already handled this update
    }
    useDevicesStore.getState().updatePosition(data.imei, data.position);
  });

  // Handle device status changes
  socket.on('device:status', (data: { imei: string; status: 'ONLINE' | 'OFFLINE' }) => {
    useDevicesStore.getState().updateStatus(data.imei, data.status);
  });

  // Handle devices added dynamically (e.g. load test simulator)
  socket.on('devices:added', (devices: Device[]) => {
    useDevicesStore.getState().addDevices(devices);
  });

  // Handle devices removed dynamically
  socket.on('devices:removed', (imeis: string[]) => {
    useDevicesStore.getState().removeDevicesByImei(imeis);
  });

  // Handle initial positions load
  socket.on('positions:all', (positions: Array<{
    imei: string;
    name: string;
    status: string;
    position: DevicePosition;
    timestamp: string;
  }>) => {
    const devicesStore = useDevicesStore.getState();
    positions.forEach((p) => {
      devicesStore.updatePosition(p.imei, p.position);
    });
  });

  // Handle alerts
  socket.on('alert:new', (alert: unknown) => {
    console.log('New alert:', alert);
    const notification = createNotificationFromAlert(alert);
    if (notification) {
      useNotificationsStore.getState().addNotification(notification);
    }
  });

  socket.on('mission:event', (event: { type: string; mission: Record<string, unknown>; originUserId?: string }) => {
    const currentUserId = useAuthStore.getState().user?.id;
    // Don't notify the user who triggered the action (e.g. dispatcher creating)
    if (event.originUserId && event.originUserId === currentUserId) return;
    const notification = createNotificationFromMissionEvent(event);
    if (notification) {
      useNotificationsStore.getState().addNotification(notification);
    }
  });

  // Handle errors
  socket.on('error', (error: { message: string }) => {
    console.error('WebSocket error:', error);
  });

  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function subscribeToDevice(imei: string): void {
  socket?.emit('subscribe:device', imei);
}

export function unsubscribeFromDevice(imei: string): void {
  socket?.emit('unsubscribe:device', imei);
}

export function subscribeAudit(): void {
  socket?.emit('subscribe:audit');
}

export function unsubscribeAudit(): void {
  socket?.emit('unsubscribe:audit');
}

export function clearAuditBuffer(): void {
  socket?.emit('clear:audit');
}

export function getSocket(): Socket | null {
  return socket;
}

export interface TrackerAuditEntry {
  imei: string;
  timestamp: number;
  rawHex?: string;
  codecId?: number;
  protocol: string;
  recordCount: number;
  remoteAddress?: string;
  positions: Array<{
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    altitude: number;
    satellites: number;
    timestamp: string;
    ignition?: boolean;
    externalVoltage?: unknown;
    batteryVoltage?: unknown;
  }>;
}
